import { format, formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { DEFAULT_TIMEZONE } from './common';

/**
 * #2091 — the ONE shared "CT-anchored datetime" module (compose + parse).
 *
 * Before this file, five call sites independently composed a Central-Time
 * (America/Chicago, `DEFAULT_TIMEZONE`) ISO datetime from a calendar day plus
 * a wall-clock time: `Helper.getCTTime`/`getCTOffset` (ui-core), the three
 * backend `convertLocalTimeToUtc` variants (manage-events-api,
 * public-events-api, manage-booking-api) and the operator quick-add
 * composition next to `normalizeEventTime` (manage-operators-api, #2090) —
 * plus inline copies of the same idiom in calendar-ui (signup,
 * signup-checkout, testevents-detail-page). They all now delegate here.
 *
 * This lib is deliberately the home because it is the only workspace lib both
 * Nx build graphs already consume: the backend cannot import `ui-core`
 * (Angular), the frontend cannot import `common-api`, and both already import
 * `@bodyanalytics/data-models-ui` (e.g. `PricingPlanI` in admin-register-api,
 * `DEFAULT_TIMEZONE` / `formatCentralTime` in the frontends). The parse side
 * (`extractWallClockTime`, #2094) lives in the same module per the #2091
 * inventory note; `@bodyanalytics/ui-core` re-exports it for existing
 * frontend imports.
 */

/**
 * UTC offset of the business timezone (Central) at the given instant:
 * `'-05:00'` (CDT) or `'-06:00'` (CST).
 *
 * ⚠️ This REPLICATES the legacy idiom byte-for-byte
 * (`format(utcToZonedTime(date, tz), 'XXX', { timeZone: tz })` — exactly what
 * `Helper.getCTOffset` and public-events-api's `getCTOffset` did), INCLUDING
 * its known runtime-TZ sensitivity: the offset is resolved at the zoned wall
 * clock re-read as a runtime-TZ instant, so for instants within ~5–6h AFTER a
 * CT DST transition, on a process whose TZ is not America/Chicago (CI and
 * prod containers run TZ=UTC), it reports the PRE-transition offset
 * (e.g. 2026-03-08T09:00:00Z → '-06:00' where the true CT offset is
 * '-05:00'). #2091's bar was byte-identical consolidation, so parity wins
 * here (PR #2106 trio ruling); the divergence window is pinned under TZ=UTC
 * in ct-time.util.spec.ts.
 *
 * Do NOT "fix" this in passing by switching to the deterministic
 * `formatInTimeZone(date, DEFAULT_TIMEZONE, 'XXX')` — that is a behavior
 * change and must ship as its own reviewed decision (see ADR-0018 §Follow-up;
 * the same decision also covers manage-operators-api's createEvent site,
 * whose historical idiom WAS `formatInTimeZone`: all of its real anchors are
 * UTC-midnight `new Date('YYYY-MM-DD')` values, which sit outside the window,
 * where the two idioms agree byte-for-byte — also pinned in the spec).
 */
export function getCTOffset(date: Date): string {
  const zonedTime = utcToZonedTime(date, DEFAULT_TIMEZONE);
  return format(zonedTime, 'XXX', { timeZone: DEFAULT_TIMEZONE });
}

/**
 * Compose the canonical CT-anchored ISO datetime string stored in
 * `TestingEvent.startTime`/`endTime`: `` `${datePart}T${wallClockTime}:00±HH:MM` ``,
 * e.g. `'2026-06-27T09:00:00-05:00'` — the Central wall clock embedded
 * LITERALLY in the characters, with the dynamic CDT/CST offset for the day.
 *
 * Pure string composition, exactly like every legacy site: `datePart` is
 * interpolated verbatim (callers pass `'YYYY-MM-DD'`, splitting any `'T…'`
 * tail themselves) and `wallClockTime` must already be 24-hour `'HH:mm'`.
 * Nothing is validated or re-zoned — a wall clock inside the spring-forward
 * gap composes unchanged, matching legacy byte-for-byte.
 *
 * @param datePart      calendar day, `'YYYY-MM-DD'` (interpolated verbatim)
 * @param wallClockTime 24-hour wall clock, `'HH:mm'`
 * @param offsetAnchor  instant the CDT/CST offset is resolved at. Defaults to
 *                      `new Date(datePart)` (UTC midnight of the day — the
 *                      dominant legacy anchor); callers that historically
 *                      anchored to the full event instant pass it explicitly.
 */
export function composeCTDateTime(
  datePart: string,
  wallClockTime: string,
  offsetAnchor: Date = new Date(datePart),
): string {
  return `${datePart}T${wallClockTime}:00${getCTOffset(offsetAnchor)}`;
}

/**
 * The CT calendar day (`'yyyy-MM-dd'`) of a stored instant — the ONE shared
 * "which day is this event on?" derivation for composition call sites.
 *
 * `TestingEvent.testEventDate` is stored as a CT-anchored instant (#2088:
 * `new Date(startTimeCT)`), so an evening event serializes past UTC midnight:
 * Aug 14 7:30 PM CDT is `'2026-08-15T00:30:00.000Z'`. Taking the date part
 * with `.split('T')[0]` / `.toISOString().split('T')[0]` reads the **UTC**
 * calendar day — one day late for any event starting ≥7 PM CDT / ≥6 PM CST —
 * which is exactly how the calendar signup funnel booked evening slots on the
 * wrong day (signup / signup-checkout `composeCTDateTime(eventDate.split('T')[0], …)`)
 * and how the operator wizard dropdown once rendered a 09/30 event as "Sep 29"
 * (operator-portal.service.ts, fixed in #2088). This helper is the
 * `formatInTimeZone(date, DEFAULT_TIMEZONE, 'yyyy-MM-dd')` idiom already used
 * by {@link ctWallClockToUtcIso}, extracted so no call site re-derives the day
 * in UTC (or the runtime TZ) again.
 *
 * A bare date-only string (`'YYYY-MM-DD'`) is already a calendar day and passes
 * through verbatim — re-zoning it would apply the documented `new Date()`
 * UTC-midnight quirk and shift it back a day in CT.
 *
 * SCOPE (PR #2165 round-2 / ADR-0018 addendum): this function is for
 * CT-anchored stored instants — `TestingEvent.testEventDate` and the
 * composeCTDateTime-shaped startTime/endTime family. It is NOT for
 * `Reservation.testEventDate`: that column is written from a bare date-picker
 * value via `new Date(dateOnly).toISOString()`
 * (manage-reservations-api.service.ts) and is a plain UTC-midnight calendar
 * day, never a CT instant — re-zoning it here would shift it back a day. The
 * one known consumer is bring-my-truck-form.component.ts, whose
 * `.split('T')[0]` is intentionally left alone (commented at the site); do
 * not "fix" it in a future sweep.
 *
 * Invalid instants throw (formatInTimeZone's RangeError) — callers on
 * composition paths must validate first and fail loudly. This is deliberately
 * NOT the display-side contract: `formatCentralTime`/`formatCentralDate`
 * DEGRADE to the `'—'` placeholder instead of throwing, because a bad row
 * must blank one cell, not crash a whole list render.
 */
export function ctCalendarDay(value: string | Date): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    return value.trim();
  }
  const instant = typeof value === 'string' ? new Date(value) : value;
  return formatInTimeZone(instant, DEFAULT_TIMEZONE, 'yyyy-MM-dd');
}

/**
 * Convert a Central wall-clock time on a given calendar day into the canonical
 * UTC ISO string (`'…Z'`, millisecond precision) — the manage-events-api
 * `convertLocalTimeToUtc` semantics, preserved from the #781 fix.
 *
 * TZ-independent: the calendar day is resolved IN CT (so a `'…T12:00:00.000Z'`
 * event date lands on the intended day regardless of the process `TZ`) and the
 * wall clock is interpreted as CT via an explicit `zonedTimeToUtc`. Note the
 * legacy quirk is preserved: a bare `'YYYY-MM-DD'` input parses as UTC
 * midnight, which is the PREVIOUS evening in CT — callers pass a mid-day
 * instant (as the #781 regression specs do) when they mean "that calendar day".
 *
 * @param testEventDate any value resolving to the intended calendar day in CT
 * @param wallClockTime 24-hour Central wall clock, `'HH:mm'`
 */
export function ctWallClockToUtcIso(testEventDate: string | Date, wallClockTime: string): string {
  const datePart = formatInTimeZone(new Date(testEventDate), DEFAULT_TIMEZONE, 'yyyy-MM-dd');
  return zonedTimeToUtc(`${datePart}T${wallClockTime}:00`, DEFAULT_TIMEZONE).toISOString();
}

/**
 * #2078/#2079 review — the ONE shared wall-clock parser for TestingEvent
 * startTime/endTime strings (moved here from `@bodyanalytics/ui-core` by
 * #2091 so the parse side lives beside the composition side; ui-core
 * re-exports it unchanged). That column is a plain `String`
 * (`prisma/schema.prisma`) with THREE real shapes in dev data:
 *
 *  1. bare 24-hour wall clock — `"08:00"` / `"9:00"` (prisma/seed.ts)
 *  2. CT-ISO datetime with the Central wall clock embedded literally —
 *     `"2025-03-28T10:00:00-05:00"` (composeCTDateTime / Helper.getCTTime)
 *  3. legacy 12-hour meridiem strings — `"9:00 PM"` (pre-#2088 poisoned rows
 *     persisted verbatim by the operator wizard; still live in dev)
 *
 * Returns the zero-padded 24-hour `"HH:MM"` reading, or null when no
 * time-shaped substring exists. Meridiem-aware: `"9:00 PM"` → `"21:00"`,
 * `"12:00 AM"` → `"00:00"`, `"12:30 PM"` → `"12:30"`. Already-24h inputs
 * (shapes 1 and 2) pass through digit-for-digit, so callers that reconstruct
 * ISO strings from the result (Helper.eventEndDateTime) are unchanged. Naive
 * digit extraction without the meridiem check silently flips PM times to AM —
 * exactly the corruption this consolidates away from two prior sibling
 * implementations (Helper.extractHHMM and the manage-events domain store's
 * formatTimeForDropdown), which both had the same blindness.
 */
export function extractWallClockTime(time: unknown): string | null {
  if (!time) return null;
  const raw = String(time);
  const match = raw.match(/(\d{1,2}):(\d{2})/);
  if (!match) return null;
  let hours = parseInt(match[1], 10);
  if (Number.isNaN(hours) || hours > 23) return null;
  const meridiem = raw.match(/\b(AM|PM)\b/i)?.[1]?.toUpperCase();
  if (meridiem === 'PM' && hours < 12) hours += 12;
  if (meridiem === 'AM' && hours === 12) hours = 0;
  return `${String(hours).padStart(2, '0')}:${match[2]}`;
}

// ── Strict (anchored) wall-clock parsing — #2120 / ADR-0018 addendum ─────────

/** Full-string 24-hour wall clock, zero-padded or not: "09:00", "9:00", "23:59". */
const STRICT_24H_PATTERN = /^([01]?\d|2[0-3]):([0-5]\d)$/;
/** Full-string 12-hour clock with meridiem: "9:00 AM", "09:00am", "12:30 PM". */
const STRICT_12H_PATTERN = /^(0?[1-9]|1[0-2]):([0-5]\d)\s*([AaPp][Mm])$/;
/**
 * Full-string ISO-ish datetime with the wall clock embedded literally —
 * the composeCTDateTime output shape ("2026-06-27T09:00:00-05:00"), plus the
 * seconds-less / fractional / Z variants that appear in stored rows.
 */
const STRICT_CT_ISO_PATTERN =
  /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d(?:\.\d+)?)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)?$/;

/**
 * #2120 — the strict/anchored sibling of {@link extractWallClockTime}, for
 * VALIDATION rather than salvage. `extractWallClockTime` deliberately matches
 * a time-shaped SUBSTRING anywhere in the value (so a reading can be rescued
 * out of a poisoned legacy string); that lenience is wrong for validators,
 * which must treat `"09:00garbage"` as unparseable rather than silently
 * accept it as 09:00. This variant matches the ENTIRE string (`^…$`) against
 * the same three real `TestingEvent.startTime`/`endTime` column shapes the
 * lenient parser documents:
 *
 *  1. bare 24-hour wall clock — `"09:00"`, and the non-padded `"9:00"` that
 *     both prisma/seed.ts and the admin edit form's dropdown emit
 *  2. legacy 12-hour meridiem strings — `"9:00 PM"` → `"21:00"`
 *  3. CT-ISO datetime with the wall clock embedded literally
 *     (`composeCTDateTime` output) — OPT-IN via `allowCtIso`, since bare
 *     wall-clock consumers (the operator quick-add normalization) must keep
 *     rejecting datetime strings
 *
 * Returns the zero-padded 24-hour `"HH:mm"` reading, or null when the whole
 * string is not exactly one of the accepted shapes.
 *
 * ADR-0018: this is the ONE shared strict wall-clock parser — the backend's
 * `normalizeEventTime` (manage-operators-api), the shared cross-field DTO
 * ordering decorator (`IsEndTimeAfterStartTime`, common-api), and the
 * frontend `eventTimeOrderValidator` (ui-core) all delegate here. Do not fork
 * these regexes again.
 */
export function extractWallClockTimeStrict(time: unknown, options?: { allowCtIso?: boolean }): string | null {
  if (typeof time !== 'string') return null;
  const value = time.trim();

  const twentyFourHour = value.match(STRICT_24H_PATTERN);
  if (twentyFourHour) {
    return `${twentyFourHour[1].padStart(2, '0')}:${twentyFourHour[2]}`;
  }

  const twelveHour = value.match(STRICT_12H_PATTERN);
  if (twelveHour) {
    let hour = parseInt(twelveHour[1], 10);
    const isPm = twelveHour[3].toLowerCase() === 'pm';
    if (isPm && hour !== 12) hour += 12;
    if (!isPm && hour === 12) hour = 0;
    return `${String(hour).padStart(2, '0')}:${twelveHour[2]}`;
  }

  if (options?.allowCtIso) {
    const iso = value.match(STRICT_CT_ISO_PATTERN);
    if (iso) {
      return `${iso[1]}:${iso[2]}`;
    }
  }

  return null;
}
